/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionFail,
  ActionRequest,
  ActionSuccess,
  MenuUiArea,
  XError,
  MenuItem,
  Ducks,
  MenuItemType,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, put, takeLeading, select } from 'redux-saga/effects';
import { Selectors as GdprSelectors } from 'core/store/ducks/gdpr';
import translate from '../../../services/i18n/translate';
import { StoreState } from '../../store';

export enum CHANNEL_FILTERS {
  mostWatched = 'mostwatched',
  allChannels = 'allchannels',
  OTTFilter = 'OTTFilter',
  justStarted = 'juststarted',
  hdChannels = 'hdchannels',
  subscribed = 'subscribed',
}

export const DEFAULT_FILTER_ID = CHANNEL_FILTERS.OTTFilter;

export interface ChannelFilter {
  name: string;
  id: CHANNEL_FILTERS | string;
  index: number;
  default: boolean;
}

// ACTIONS
export enum ActionType {
  GET_CHANNEL_FILTERS = 'watchTv/GET_CHANNEL_FILTERS',
  GET_CHANNEL_FILTERS_SUCCESS = 'watchTv/GET_CHANNEL_FILTERS_SUCCESS',
  GET_CHANNEL_FILTERS_FAIL = 'watchTv/GET_CHANNEL_FILTERS_FAIL',
}

type GetChannelFilters = ActionRequest<ActionType.GET_CHANNEL_FILTERS>;
type GetChannelFiltersSuccess = ActionSuccess<ActionType.GET_CHANNEL_FILTERS_SUCCESS, ChannelFilter[]>;
type GetChannelFiltersFail = ActionFail<ActionType.GET_CHANNEL_FILTERS_FAIL>;

type Actions = GetChannelFilters | GetChannelFiltersSuccess | GetChannelFiltersFail;

const Actions = {
  getChannelFilters: (): GetChannelFilters => ({ type: ActionType.GET_CHANNEL_FILTERS }),
  getChannelFiltersSuccess: (channelFilters: ChannelFilter[]): GetChannelFiltersSuccess => ({
    type: ActionType.GET_CHANNEL_FILTERS_SUCCESS,
    payload: channelFilters,
  }),
  getChannelFiltersFail: (error: XError): GetChannelFiltersFail => ({
    type: ActionType.GET_CHANNEL_FILTERS_FAIL,
    error,
  }),
};

export const ActionCreators = {
  getChannelFilters: Actions.getChannelFilters,
};

//
// REDUCER
//

type ReducerState = {
  filters: ChannelFilter[];
  processing: boolean;
  processed: boolean;
  error?: XError;
};

const initialState: ReducerState = {
  filters: [],
  processing: false,
  processed: false,
};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.GET_CHANNEL_FILTERS:
      return {
        ...state,
        processing: false,
        processed: true,
        error: undefined,
      };
    case ActionType.GET_CHANNEL_FILTERS_SUCCESS:
      return {
        ...state,
        filters: action.payload,
        processing: false,
        processed: true,
      };
    case ActionType.GET_CHANNEL_FILTERS_FAIL:
      return {
        filters: initialState.filters,
        processing: false,
        processed: true,
        error: action.error,
      };

    default:
      return state;
  }
}

//
// SAGAS
//

// private sagas
function* getChannelFilters() {
  // let defaultFilter = DEFAULT_FILTER_ID;
  const vendorFilters: MenuItem | undefined = yield select(
    Ducks.Menu.Selectors.getMainMenuItem,
    MenuUiArea.EPG_FILTERS
  );

  const epgHiddenFilters = vendorFilters?.children.filter((filter) => filter.type === MenuItemType.EPG_HIDDEN_FILTER);
  const epgDefaultFilters = vendorFilters?.children.filter((filter) => filter.type === MenuItemType.EPG_DEFAULT_FILTER);
  const epgFilters = vendorFilters?.children.filter(
    (filter) =>
      filter.type === MenuItemType.EPG_FILTER &&
      filter.name !== CHANNEL_FILTERS.justStarted &&
      filter.name !== CHANNEL_FILTERS.hdChannels
  );

  const filtersToRemove = epgHiddenFilters?.map((filter) => filter.name);

  const filters: { name: string; id: CHANNEL_FILTERS | string }[] = [];

  const isRecommEnabled: boolean = yield select(Ducks.Recommendations.Selectors.isFeatureEnabled);
  const hasAgreedToConsent: boolean = yield select(GdprSelectors.hasAgreedToConsent);
  if (!filtersToRemove?.includes(CHANNEL_FILTERS.mostWatched) && isRecommEnabled && hasAgreedToConsent) {
    filters.push({
      name: `${translate['watchtv.mostWatched']}`,
      id: CHANNEL_FILTERS.mostWatched,
    });
  }

  if (!filtersToRemove?.includes(CHANNEL_FILTERS.allChannels)) {
    filters.push({
      name: `${translate['watchtv.allChannels']}`,
      id: CHANNEL_FILTERS.allChannels,
    });
  }

  if (!filtersToRemove?.includes(CHANNEL_FILTERS.OTTFilter)) {
    filters.push({
      name: `${translate['watchtv.OTTFilter']}`,
      id: CHANNEL_FILTERS.OTTFilter,
    });
  }

  if (!filtersToRemove?.includes(CHANNEL_FILTERS.justStarted)) {
    filters.push({
      name: `${translate['watchtv.justStarted']}`,
      id: CHANNEL_FILTERS.justStarted,
    });
  }

  if (!filtersToRemove?.includes(CHANNEL_FILTERS.hdChannels)) {
    filters.push({
      name: `${translate['watchtv.hdChannels']}`,
      id: CHANNEL_FILTERS.hdChannels,
    });
  }

  if (epgFilters?.length) {
    const tempArray = [...epgFilters];
    tempArray.sort((a, b) => {
      if (a.channelId !== undefined && b.channelId !== undefined) {
        return a.channelId - b.channelId;
      }
      if (a.channelId === undefined && b.channelId === undefined) {
        return 0;
      }
      return a.channelId === undefined ? 1 : -1;
    });

    tempArray.forEach((e) => {
      filters.push({ name: e.name, id: e.name });
    });
  }

  if (!filtersToRemove?.includes(CHANNEL_FILTERS.subscribed)) {
    filters.push({
      name: `${translate['watchtv.subscribed']}`,
      id: CHANNEL_FILTERS.subscribed,
    });
  }

  let defaultFilter: string | undefined;

  if (epgDefaultFilters?.length) {
    defaultFilter = filters.find((filter) => filter.id === epgDefaultFilters[0].name)?.id;
  }
  if (!defaultFilter) {
    defaultFilter = DEFAULT_FILTER_ID;
  }

  const indexedFilters: ChannelFilter[] = filters.map((filter, index) => {
    return { ...filter, index, default: filter.id === defaultFilter };
  });

  yield put(Actions.getChannelFiltersSuccess(indexedFilters));
}

// public sagas
export function* saga(): SagaIterator {
  yield all([takeLeading(ActionType.GET_CHANNEL_FILTERS, getChannelFilters)]);
}

//
// SELECTORS
//

export const Selectors = {
  processed: (state: StoreState): boolean | undefined => state.watchTv.channelFilters?.processed,
  error: (state: StoreState): XError | undefined => state.watchTv.channelFilters?.error,
  channelFilters: (state: StoreState): ChannelFilter[] => state.watchTv.channelFilters?.filters,
};
